import { User } from 'firebase/auth';
import { apolloClient } from '@lib/apollo';
import Router from 'next/router';
import { FirebaseProvider, firebaseProvider } from './firebase';

class AuthProvider {
  private firebaseProvider: FirebaseProvider;
  // default timeout duration: 24hours
  timeoutDuration = 24 * 60 * 60 * 1000;

  constructor(initializedFirebaseProvider: FirebaseProvider) {
    this.firebaseProvider = initializedFirebaseProvider;
  }

  authenticateUser = async (email: string, password: string): Promise<void> => {
    await this.firebaseProvider.firebaseAuth.signInWithEmailAndPassword(firebaseProvider.auth, email, password);
  };

  sendPasswordResetEmail = async (email: string): Promise<void> => {
    await this.firebaseProvider.firebaseAuth.sendPasswordResetEmail(firebaseProvider.auth, email);
  };

  isUserLoggedIn = (callback: (user: User | null) => void) => {
    return this.firebaseProvider.firebaseAuth.onAuthStateChanged(firebaseProvider.auth, callback);
  };

  getAuthHeaders = async () => {
    const { currentUser } = this.firebaseProvider.auth;

    if (currentUser === null) {
      throw new Error('User not logged in.');
    }

    const firebaseIdToken = await currentUser.getIdToken(/* forceRefresh */ true);

    return `Bearer ${firebaseIdToken}`;
  };

  logoutUser = async (): Promise<void> => {
    await this.firebaseProvider.firebaseAuth.signOut(firebaseProvider.auth);
  };

  logoutAfterTimeOut = (reset: () => void) => {
    return () => {
      reset();
      Promise.all([apolloClient.clearStore(), apolloClient.cache.reset(), this.logoutUser()]).then(() => {
        Router.push('/login').then(() => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          window.location.reload(true);
        });
      });
    };
  };

  setTimeOutLogout = async (reset: () => void): Promise<NodeJS.Timeout> => {
    const logoutHandler = this.logoutAfterTimeOut(reset);
    return setTimeout(logoutHandler, this.timeoutDuration);
  };
}

export const authProvider = new AuthProvider(firebaseProvider);
